import { useState } from 'react';
import { Badge, cn } from '@playerbase/ui';
import type { FormFieldSeries } from '@playerbase/types';

// ---------------------------------------------------------------------------
// Field summary list.
//
// The per-question read-out shared by the feedback form summary (above a
// form's submission list) and the playtest event drill-down (under a selected
// event card). One block per question: rating and NPS render a distribution
// histogram (with the mean), single-choice / severity / yes-no render
// labelled bars, and open-text questions show an expandable list of answers.
//
// Purely presentational - the only state it owns is the open-text expand
// toggle. Each caller supplies its own header (the form summary shows an
// overall sentiment band; the playtest drill-down's header is the event card
// the studio clicked). All copy is plain product language; the data here is
// derived on read and the derivation is never named in the UI.
// ---------------------------------------------------------------------------

export function FieldSummaryList({ fields }: { fields: FormFieldSeries[] }): JSX.Element {
  return (
    <div className="flex flex-col divide-y divide-border">
      {fields.map((series) => (
        <FieldSeriesBlock
          key={`${series.kind}:${series.title}:${series.retired ? 'r' : 'a'}`}
          series={series}
        />
      ))}
    </div>
  );
}

function FieldSeriesBlock({ series }: { series: FormFieldSeries }): JSX.Element {
  return (
    <div className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="truncate text-sm font-medium text-text">{series.title}</span>
          <span className="text-xs text-text-muted">
            {series.responseCount} {series.responseCount === 1 ? 'response' : 'responses'}
            {meanSuffix(series)}
          </span>
        </div>
        {series.retired && (
          <Badge tone="neutral" title="No longer on the form">
            Retired
          </Badge>
        )}
      </div>
      <SeriesBody series={series} />
    </div>
  );
}

// The mean is only meaningful for the numeric kinds. Shown inline next to the
// response count so the header stays compact.
function meanSuffix(series: FormFieldSeries): string {
  const summary = series.kind === 'nps' ? series.nps : series.rating;
  if (
    (series.kind === 'rating' || series.kind === 'nps') &&
    summary !== null &&
    summary.mean !== null
  ) {
    return ` · avg ${summary.mean}`;
  }
  return '';
}

function SeriesBody({ series }: { series: FormFieldSeries }): JSX.Element | null {
  if (series.kind === 'rating' && series.rating !== null) {
    // Highest score first (5 -> 1) so the bars read top-down like the scale.
    return (
      <BarList
        rows={[...series.rating.distribution].reverse().map((b) => ({
          label: String(b.value),
          count: b.count,
        }))}
      />
    );
  }
  if (series.kind === 'nps' && series.nps !== null) {
    // Highest score first (10 -> 1).
    return (
      <BarList
        rows={[...series.nps.distribution].reverse().map((b) => ({
          label: String(b.value),
          count: b.count,
        }))}
      />
    );
  }
  if (series.kind === 'select' && series.select !== null) {
    if (series.select.options.length === 0) return null;
    return (
      <BarList
        rows={series.select.options.map((o) => ({
          label: o.label,
          count: o.count,
        }))}
      />
    );
  }
  if (series.kind === 'severity' && series.severity !== null) {
    return <BarList rows={series.severity.map((s) => ({ label: s.label, count: s.count }))} />;
  }
  if (series.kind === 'binary' && series.binary !== null) {
    // Stored answers are canonical true/false; v1 labels them Yes / No.
    // Custom trueLabel/falseLabel overrides are not surfaced here yet.
    return (
      <BarList
        rows={[
          { label: 'Yes', count: series.binary.trueCount },
          { label: 'No', count: series.binary.falseCount },
        ]}
      />
    );
  }
  if (series.kind === 'short_text' || series.kind === 'long_text') {
    if (series.responses === null || series.responses.length === 0) return null;
    return <OpenTextResponses responses={series.responses} />;
  }
  return null;
}

// How many open-text answers to reveal per "Load more" click. A form on a
// large server can gather hundreds, so we page them in rather than paint the
// whole list at once.
const RESPONSES_PAGE = 20;

// Collapsed by default - open-text answers can be long, so the studio opts in
// to reading them. Lists every answer to this question, newest first, paged.
// The open/close height is animated via the grid-rows 0fr->1fr trick so it
// stays smooth without hardcoding a max-height; the content stays mounted, so
// it's aria-hidden while collapsed.
function OpenTextResponses({ responses }: { responses: string[] }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(RESPONSES_PAGE);
  const shown = responses.slice(0, visibleCount);
  const remaining = responses.length - shown.length;

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="self-start text-xs font-medium text-accent hover:underline"
      >
        {expanded ? 'Hide responses' : 'Show responses'}
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden" aria-hidden={!expanded}>
          <ul className="flex flex-col gap-2 border-l border-border pl-3">
            {shown.map((text, idx) => (
              <li key={idx} className="whitespace-pre-wrap text-sm text-text-muted">
                {text}
              </li>
            ))}
          </ul>
          {remaining > 0 && (
            <button
              type="button"
              onClick={() => setVisibleCount((c) => c + RESPONSES_PAGE)}
              className="mt-2 text-xs font-medium text-accent hover:underline"
            >
              Load more ({remaining} more)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface BarRow {
  label: string;
  count: number;
}

// Horizontal labelled bars sharing one scale (the largest count fills the
// track). Mirrors the lightweight track/fill primitive used by the poll
// group card rather than pulling in a chart library.
function BarList({ rows }: { rows: BarRow[] }): JSX.Element {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  return (
    <ul className="flex flex-col gap-1.5">
      {rows.map((row, idx) => {
        const pct = max > 0 ? Math.round((row.count / max) * 100) : 0;
        return (
          <li key={`${row.label}-${idx}`} className="flex items-center gap-3">
            <span className="w-24 shrink-0 truncate text-xs text-text-muted">{row.label}</span>
            <span
              aria-hidden
              className="block h-1.5 flex-1 overflow-hidden rounded-full bg-surface-3"
            >
              <span
                className={cn(
                  'block h-full rounded-full transition-[width] duration-300 ease-out',
                  row.count > 0 ? 'bg-accent' : 'bg-transparent',
                )}
                style={{ width: `${pct}%` }}
              />
            </span>
            <span className="w-8 shrink-0 text-right text-xs tabular-nums text-text-muted">
              {row.count}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
